import { useEffect, useState } from "react";
import PageHeader from "../../components/PageHeader";
import { PaginationControls } from "../../components/PaginationControls";
import { LMSBASEURL } from "../../config/apiUrls";
import { SkillsCell } from "../../components/student-profile/student-grid-view";
import Swal from "sweetalert2";
import useDebounce from "../../hooks/use-debounce";
import { getStatusBadgeVariant, showStatusMsg } from "../../utils/utils";
import { Badge } from "react-bootstrap";

const StudentReports = () => {
  const [activeTab, setActiveTab] = useState("job");
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);
  const [offerLetterFilter, setOfferLetterFilter] = useState("");
  const [isExporting,setIsExporting] = useState(false)
  const instituteId = sessionStorage.getItem("instituteId")
  const token = process.env.REACT_APP_API_TOKEN;

  const fetchStudents = async (page = 1, per_page = 10) => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        page: page,
        per_page: per_page,
      });

      if (offerLetterFilter) queryParams.append("offer_letter_acceptance", offerLetterFilter);
      if (debouncedSearch) queryParams.append("search", debouncedSearch);

      const response = await fetch(
        `${LMSBASEURL}/api/students/placements/${instituteId}?${queryParams.toString()}`,
        {
          method: "GET",
          headers: {
            Authorization: token,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );

      const result = await response.json();
      if (result.success) {
        const formattedRows = result.data.map((item) => ({
          studentInfo: item.student_info,
          academicInfo: {
            degree: item.academic_info?.degree || "-",
            year: item.academic_info?.studying_year || "-",
            passingYear: item.academic_info?.passing_year || "-",
          },
          skills: {
            display: item.skills?.split(", ").slice(0, 2).join(", ") + " +" + (item.skills?.split(", ").length - 2),
            fullList: item.skills?.split(", "),
          },
          placementInfo: item.placement_info?.[0] || {}, // latest placement
          jobApplications: item.job_applications || {},
        }));

        setRows(formattedRows);
        setPagination(result.pagination);
      }
    } catch (error) {
      console.error("Error fetching students:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStudents(1, 10);
  }, [offerLetterFilter, debouncedSearch]);

  const handleExport = async () => {
    try {
      setIsExporting(true)
      const response = await fetch(
        `${LMSBASEURL}/api/students/placements/${instituteId}/export`,
        {
          method: "GET",
          headers: {
            Authorization: token,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to export");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `student_reports.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setIsExporting(false)
    } catch (err) {
      console.error("Export error:", err);
      Swal.fire({
        icon:"error",
        text:"Failed to export file."
      })
    } finally{
      setIsExporting(false)
    }
  };

  return (
    <>
      <div className="col-12">
        <PageHeader
          title="Placement Reports"
          breadcrumbs={[
            { label: "Dashboard", path: "/institute-dashboard" },
            { label: "Placement Reports", active: true },
          ]}
        />
      </div>

      <main>
        <ul className="nav nav-tabs">
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "job" ? "active" : ""}`}
              onClick={() => setActiveTab("job")}
            >
              Jobs
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "internship" ? "active" : ""}`}
              onClick={() => setActiveTab("internship")}
            >
              Internships
            </button>
          </li>
        </ul>

        <div className="row mb-4 bg-white rounded py-md-3 mx-md-1 border">
          <div className="col-lg-3">
            <div className="mb-2">
              <label className="form-label fw-bold small mb-1">By Name:</label>
              <input
                type="text"
                className="form-control w-100"
                placeholder="Search student..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="col-lg-3">
            <div className="mb-2">
              <label className="form-label fw-bold small mb-1">Offer Letter:</label>
              <select
                className="form-select w-100"
                value={offerLetterFilter}
                onChange={(e) => setOfferLetterFilter(e.target.value)}
              >
                <option value="">All</option>
                <option value="Pending">Pending</option>
                <option value="Accepted">Accepted</option>
                <option value="Rejected">Rejected</option>
              </select>
            </div>
          </div>
          {rows.length!= 0 &&
          <div className="col-lg-6">
            <div className="d-flex justify-content-end align-items-center mt-4 mb-2 w-100">
              <button className="btn btn-success" disabled={isExporting} onClick={handleExport}>
                {isExporting ?
                <>
                <div className="spinner-border spinner-border-sm text-light pr-1" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
                {" "}Please Wait
                </>: "Export"}
              </button>
            </div>
          </div>}
        </div>

        <section className="card">
          <div className="card-body p-0">
            <div className="table-responsive">
              {loading ? (
              <div className="d-flex flex-column justify-content-center align-items-center py-5" style={{ height: "300px" }}>
                <div className="spinner-border spinner-border-lg" style={{ width: "3rem", height: "3rem" }} role="status"></div>
                <span className="mt-3 text-primary fw-bold">Loading data...</span>
              </div>
              ) : (
                <>
                {activeTab === "job"?
                <table className="table table-bordered align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th colSpan={3} style={{ backgroundColor: "#c0cfdd", textAlign: "center" }}>
                        Student Info
                      </th>
                      <th colSpan={3} style={{ backgroundColor: "#c0cfdd", textAlign: "center" }}>
                        Academic Info
                      </th>
                      <th style={{ backgroundColor: "#c0cfdd", textAlign: "center" }}>Skills</th>
                      <th colSpan={8} style={{ backgroundColor: "#c0cfdd", textAlign: "center" }}>
                        Placement Info
                      </th>
                      <th colSpan={3} style={{ backgroundColor: "#c0cfdd", textAlign: "center" }}>
                        Job Application
                      </th>
                    </tr>

                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Degree</th>
                      <th className="tableNoWrap">Studying Year</th>
                      <th className="tableNoWrap">Passing Year</th>
                      <th>Skills</th>
                      <th>Company</th>
                      <th>Role</th>
                      <th className="tableNoWrap">{activeTab === "job" ? "Package (LPA)" : "Stipend"}</th>
                      <th>Type</th>
                      <th className="tableNoWrap">Offer Status</th>
                      <th className="tableNoWrap">Offer Date</th>
                      <th className="tableNoWrap">Joining Date</th>
                      <th className="tableNoWrap">Offer Letter</th>
                      <th>Applied</th>
                      <th className="tableNoWrap">Shortlisted</th>
                      <th>Rejected</th>
                    </tr>
                  </thead>

                  <tbody>
                    {rows.length === 0 ? (
                      <tr>
                        <td colSpan="16" className="text-center py-3">
                          No records found
                        </td>
                      </tr>
                    ) : (
                      rows.map((r, i) => (
                        <tr key={i}>
                          <td className="tableNoWrap">{r.studentInfo.name}</td>
                          <td>{r.studentInfo.email}</td>
                          <td>{r.studentInfo.phone}</td>
                          <td className="tableNoWrap">{r.academicInfo.degree}</td>
                          <td>{r.academicInfo.year}</td>
                          <td>{r.academicInfo.passingYear}</td>
                          <td className="tableNoWrap" style={{maxWidth:300}}>
                            {r?.skills?.fullList[0]==""? "-" :
                            <SkillsCell skillsCsv={r?.skills?.fullList} numShow={1} isApp />}
                          </td>
                          <td>{r.placementInfo.company_name}</td>
                          <td className="tableNoWrap">{r.placementInfo.role}</td>
                          <td>{activeTab === "job" ? r.placementInfo.package : r.placementInfo.stipend}</td>
                          <td className="tableNoWrap">{r.placementInfo.placement_type}</td>
                          <td>
                            <Badge
                              bg={
                                getStatusBadgeVariant(r.placementInfo.placement_status)?.startsWith("custom")
                                  ? undefined
                                  : getStatusBadgeVariant(r.placementInfo.placement_status)
                              }
                              className={
                                getStatusBadgeVariant(r.placementInfo.placement_status)?.startsWith("custom")
                                  ? getStatusBadgeVariant(r.placementInfo.placement_status)
                                  : ""
                              }
                            >
                              {showStatusMsg[r.placementInfo.placement_status?.toLowerCase()] ||
                                (r.placementInfo.placement_status
                                  ? r.placementInfo.placement_status.charAt(0).toUpperCase() + r.placementInfo.placement_status.slice(1)
                                  : "-")}
                            </Badge>
                          </td>
                          <td className="tableNoWrap">{r.placementInfo.offer_date}</td>
                          <td>{r.placementInfo.joining_date}</td>
                          <td>{r.placementInfo.offerLetterStatus || "-"}</td>
                          <td>{r.jobApplications.total_applied}</td>
                          <td>{r.jobApplications.shortlisted}</td>
                          <td>{r.jobApplications.rejected}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>:
                <table className="table table-bordered align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th colSpan={3} style={{ backgroundColor: "#c0cfdd", textAlign: "center" }}>
                        Student Info
                      </th>
                      <th colSpan={3} style={{ backgroundColor: "#c0cfdd", textAlign: "center" }}>
                        Academic Info
                      </th>
                      <th style={{ backgroundColor: "#c0cfdd", textAlign: "center" }}>Skills</th>
                      <th colSpan={8} style={{ backgroundColor: "#c0cfdd", textAlign: "center" }}>
                        Placement Info
                      </th>
                      <th colSpan={3} style={{ backgroundColor: "#c0cfdd", textAlign: "center" }}>
                        Job Application
                      </th>
                    </tr>

                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Degree</th>
                      <th className="tableNoWrap">Studying Year</th>
                      <th className="tableNoWrap">Passing Year</th>
                      <th>Skills</th>
                      <th>Company</th>
                      <th>Role</th>
                      <th className="tableNoWrap">Stipend</th>
                      <th>Type</th>
                      <th className="tableNoWrap">Offer Status</th>
                      <th className="tableNoWrap">Offer Date</th>
                      <th className="tableNoWrap">Joining Date</th>
                      <th className="tableNoWrap">Offer Letter</th>
                      <th>Applied</th>
                      <th className="tableNoWrap">Shortlisted</th>
                      <th>Rejected</th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr>
                      <td colSpan="16" className="text-center py-3">
                        No records found
                      </td>
                    </tr>
                  </tbody>
                </table>
                }
                </>
              )}
            </div>
          </div>
        </section>

        {pagination && pagination.total >= 10 && (
          <div className="d-flex justify-content-end mt-3">
            <PaginationControls
              pagination={pagination}
              onPageChange={(page) => fetchStudents(page, pagination.per_page)}
            />
          </div>
        )}
      </main>
    </>
  );
};

export default StudentReports;
